import React from "react";
import { useLocalization } from "@fluent/react";
import { Checkbox, Select } from "@narmi/design_system";
import styles from "./OrganizationRoles.module.scss";
import {
  DEFAULT_FUTURE_ACCOUNTS_STATE,
  FUTURE_ACCOUNTS_ERROR,
  FutureAccountsState,
  RoleFormData,
  getFutureAccountReferenceOptions,
} from "./RoleFormUtils";

interface RoleFutureAccountsFieldProps {
  /** Set by `ContextForm.Field`: the option's state, and the form's change handler and error. */
  field?: string;
  value?: FutureAccountsState;
  onChange?: (value: FutureAccountsState) => void;
  error?: unknown;
  /** The accounts that can be picked are the selected Move money ones. */
  formData: Pick<RoleFormData, "accounts" | "accountPermissions">;
}

/**
 * "Access to all accounts opened later": accounts opened after the role is saved get the
 * permissions of the account picked here. Render it inside a `ContextForm.Field` that validates
 * with `validateFutureAccounts`.
 */
export default function RoleFutureAccountsField({
  value = DEFAULT_FUTURE_ACCOUNTS_STATE,
  onChange = () => {},
  error,
  formData,
}: RoleFutureAccountsFieldProps) {
  const { l10n } = useLocalization();
  const options = getFutureAccountReferenceOptions(formData);

  return (
    <div className={styles.futureAccountsCard}>
      <Checkbox
        kind="condensed"
        label={l10n.getString("org-role-future-accounts-label")}
        checked={value.enabled}
        onChange={() => onChange({ ...value, enabled: !value.enabled })}
      />
      <div className={styles.futureAccountsSelect}>
        <Select
          value={value.referenceId}
          onChange={(referenceId: string) =>
            onChange({ ...value, referenceId })
          }
          disabled={!value.enabled || options.length === 0}
          label={l10n.getString("org-role-future-accounts-placeholder")}
          errorText={
            error === FUTURE_ACCOUNTS_ERROR.NO_REFERENCE
              ? l10n.getString("org-role-future-accounts-required")
              : undefined
          }
        >
          {options.map((account) => (
            <Select.Item key={account.id} value={String(account.id)}>
              {l10n.getString("org-role-future-accounts-same-as", {
                account: account.getShortDescription(false),
              })}
            </Select.Item>
          ))}
        </Select>
      </div>
    </div>
  );
}
